<script lang="ts">
  import Dialog from '@components/dialog.svelte';

  interface Props {
    /** Controlled by the parent. */
    open?: boolean;
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Style the confirm button as destructive (red). */
    danger?: boolean;
    onconfirm?: () => void;
    oncancel?: () => void;
  }

  let {
    open = false,
    title = 'Are you sure?',
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
    onconfirm,
    oncancel
  }: Props = $props();

  // Focus the confirm button when the dialog opens so Enter is enough to
  // confirm. Native showModal() reclaims focus, so defer to the next frame
  // (after Dialog has shown it) to land focus on the button.
  let confirmBtn = $state<HTMLButtonElement>();
  $effect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => confirmBtn?.focus());
    return () => cancelAnimationFrame(id);
  });
</script>

<Dialog {open} {title} onclose={() => oncancel?.()}>
  <div class="confirmBody">
    <p class="message">{message}</p>

    <div class="actions">
      <button type="button" class="btn cancel" onclick={() => oncancel?.()}>
        {cancelLabel}
      </button>
      <button
        type="button"
        class="btn confirm"
        class:danger
        bind:this={confirmBtn}
        onclick={() => onconfirm?.()}
      >
        {confirmLabel}
      </button>
    </div>
  </div>
</Dialog>

<style lang="scss">
  .confirmBody {
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 420px;
    padding-bottom: 24px;

    .message {
      margin: 0;
      color: var(--text-secondary);
      font-family: var(--font-primary);
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5rem;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;

      .btn {
        padding: 10px 16px;
        border: none;
        border-radius: 99px;
        font-family: var(--font-primary);
        font-size: 1rem;
        font-weight: 600;
        line-height: 1rem;
        cursor: pointer;
      }

      .cancel {
        background: var(--subtile-devider);
        color: var(--text-primary);
      }

      .confirm {
        background: var(--accent);
        color: #fff;

        &.danger {
          background: var(--warning-bright);
        }
      }
    }
  }
</style>
